import React, { useCallback, useMemo } from 'react';
import {
  Activity,
  AlertCircle,
  Calendar,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  ClipboardList,
  Clock,
  CreditCard,
  Download,
  Eye,
  FileCheck,
  FileText,
  History,
  Loader2,
  MessageCircle,
  Pill,
  Stethoscope,
  Truck,
  UserCog,
  UserPlus,
  Video,
  X,
} from 'lucide-react';
import { PatientTimelineEvent } from '@/lib/types';
import { patientService } from '@/services/patientService';
import { useClinic } from '@/contexts/ClinicContext';
import { formatToLocalDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PatientAuditTrailProps {
  patientId: string;
  limit?: number;
  enablePagination?: boolean;
  showHeader?: boolean;
  showFilters?: boolean;
}

const DEFAULT_LIMIT = 10;

const BASE_EVENT_TYPE_OPTIONS = [
  { value: 'patient_created', label: 'Patient Created' },
  { value: 'patient_updated', label: 'Patient Updated' },
  { value: 'patient_viewed', label: 'Patient Profile Viewed' },
  { value: 'prescription_created', label: 'Order Created' },
  { value: 'prescription_paid', label: 'Order Paid' },
  { value: 'prescription_delivered', label: 'Order Delivered' },
  { value: 'mdi_case_created', label: 'MDI Case Created' },
  { value: 'mdi_case_completed', label: 'MDI Case Completed' },
  { value: 'intake_initiated', label: 'Intake Initiated' },
  { value: 'intake_completed', label: 'Intake Completed' },
  { value: 'appointment_booked', label: 'Appointment Booked' },
  { value: 'appointment_joined', label: 'Appointment Joined' },
  { value: 'lab_order_created', label: 'Lab Order Created' },
  { value: 'lab_results_received', label: 'Lab Results Received' },
  { value: 'consent_signed', label: 'Consent Signed' },
  { value: 'encounter_started', label: 'Encounter Started' },
  { value: 'encounter_completed', label: 'Encounter Completed' },
  { value: 'message_sent', label: 'Message Sent' },
  { value: 'refill_requested', label: 'Refill Requested' },
  { value: 'payment_received', label: 'Payment Received' },
  { value: 'other', label: 'Other' },
];

const eventConfig: Record<string, { icon: React.ReactNode; color: string }> = {
  patient_created: { icon: <UserPlus className="h-3.5 w-3.5" />, color: 'text-blue-500' },
  patient_updated: { icon: <UserCog className="h-3.5 w-3.5" />, color: 'text-emerald-500' },
  patient_viewed: { icon: <Eye className="h-3.5 w-3.5" />, color: 'text-slate-500' },
  prescription_created: { icon: <Pill className="h-3.5 w-3.5" />, color: 'text-green-600' },
  prescription_paid: { icon: <CreditCard className="h-3.5 w-3.5" />, color: 'text-emerald-600' },
  prescription_delivered: { icon: <Truck className="h-3.5 w-3.5" />, color: 'text-purple-600' },
  mdi_case_created: { icon: <FileText className="h-3.5 w-3.5" />, color: 'text-indigo-600' },
  mdi_case_completed: { icon: <CheckCircle className="h-3.5 w-3.5" />, color: 'text-green-600' },
  intake_initiated: { icon: <ClipboardList className="h-3.5 w-3.5" />, color: 'text-yellow-600' },
  intake_completed: { icon: <CheckCircle className="h-3.5 w-3.5" />, color: 'text-green-600' },
  appointment_booked: { icon: <Calendar className="h-3.5 w-3.5" />, color: 'text-blue-600' },
  appointment_joined: { icon: <Video className="h-3.5 w-3.5" />, color: 'text-purple-600' },
  lab_order_created: { icon: <Activity className="h-3.5 w-3.5" />, color: 'text-cyan-600' },
  lab_results_received: { icon: <FileCheck className="h-3.5 w-3.5" />, color: 'text-green-600' },
  consent_signed: { icon: <FileText className="h-3.5 w-3.5" />, color: 'text-indigo-600' },
  encounter_started: { icon: <Stethoscope className="h-3.5 w-3.5" />, color: 'text-blue-600' },
  encounter_completed: { icon: <CheckCircle className="h-3.5 w-3.5" />, color: 'text-green-600' },
  message_sent: { icon: <MessageCircle className="h-3.5 w-3.5" />, color: 'text-gray-600' },
  refill_requested: { icon: <AlertCircle className="h-3.5 w-3.5" />, color: 'text-orange-600' },
  payment_received: { icon: <CreditCard className="h-3.5 w-3.5" />, color: 'text-green-600' },
  other: { icon: <Clock className="h-3.5 w-3.5" />, color: 'text-slate-400' },
};

const PROGRAM_LABEL_MAP: Record<string, string> = {
  trt: 'TRT',
  hrt: 'HRT',
  glp1: 'GLP1',
  'glp-1': 'GLP1',
  weightwise: 'WeightWise',
  'weight-loss': 'Weight Loss',
  peptide: 'Peptide',
  nad: 'NAD',
  general: 'General',
};

const formatProgramLabel = (program: string) => {
  const normalized = String(program || '').trim().toLowerCase();
  return PROGRAM_LABEL_MAP[normalized] || program;
};

const formatTimelineProgramText = (text?: string | null) => {
  if (!text) return text || '';
  return text.replace(/\bfor\s+([a-z0-9-]+)\s+program\b/gi, (_match, program) => {
    return `for ${formatProgramLabel(program)} Program`;
  });
};

const PatientAuditTrail: React.FC<PatientAuditTrailProps> = ({
  patientId,
  limit = DEFAULT_LIMIT,
  enablePagination = false,
  showHeader = true,
  showFilters = true,
}) => {
  const { clinic } = useClinic();
  const isGuided = clinic?.clinic_archetype === 'guided';
  const eventTypeOptions = useMemo(() =>
    BASE_EVENT_TYPE_OPTIONS.map(et =>
      isGuided && et.value === 'intake_initiated' ? { ...et, label: 'Enrollment Initiated' } :
      isGuided && et.value === 'intake_completed' ? { ...et, label: 'Enrollment Completed' } :
      et
    ), [isGuided]);

  const [events, setEvents] = React.useState<PatientTimelineEvent[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(false);
  const [expandedEventIds, setExpandedEventIds] = React.useState<Set<string>>(new Set());
  const [selectedEventType, setSelectedEventType] = React.useState<string>('all');
  const [startDate, setStartDate] = React.useState<string>('');
  const [endDate, setEndDate] = React.useState<string>('');

  const toggleExpandedEvent = useCallback((eventId: string) => {
    setExpandedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  }, []);

  const fetchTimeline = useCallback(
    async (reset = false) => {
      try {
        setIsLoading(true);
        const response = await patientService.getPatientTimeline(patientId, {
          limit,
          offset: reset ? 0 : offset,
          eventTypes: selectedEventType === 'all' ? undefined : [selectedEventType],
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        });

        if (response?.success) {
          const nextEvents = response.data?.events || [];
          setHasMore(Boolean(response.data?.hasMore));
          setEvents(prev => (reset ? nextEvents : [...prev, ...nextEvents]));
        }
      } catch (error) {
        console.error('Error fetching patient timeline:', error);
        if (reset) {
          setEvents([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [patientId, limit, offset, selectedEventType, startDate, endDate],
  );

  React.useEffect(() => {
    if (patientId) {
      setOffset(0);
      fetchTimeline(true);
    }
  }, [patientId, selectedEventType, startDate, endDate, fetchTimeline]);

  const handleLoadMore = async () => {
    const nextOffset = offset + limit;
    setOffset(nextOffset);
    await fetchTimeline();
  };

  const handleExport = useCallback(() => {
    if (!events.length) return;

    const csvHeaders = ['Date', 'Event', 'Description', 'Actor', 'Type'];
    const csvRows = events.map(event => [
      event.event_timestamp,
      formatTimelineProgramText(event.title),
      formatTimelineProgramText(event.description || ''),
      event.actor_name || event.actor_type || '',
      event.event_type,
    ]);

    const csvContent = [csvHeaders, ...csvRows]
      .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `patient-timeline-${patientId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [events, patientId]);

  const timelineTitle = useMemo(
    () => (enablePagination ? 'Activity Timeline' : 'Recent Activity'),
    [enablePagination],
  );

  const hasActiveFilters = selectedEventType !== 'all' || !!startDate || !!endDate;
  const isInitialLoading = isLoading && events.length === 0;
  const isEmpty = !isInitialLoading && (!events || events.length === 0);

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      {showHeader && (
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">{timelineTitle}</h3>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      )}

      {showFilters && (
        <div className="px-4 py-3 border-b border-slate-100 space-y-2">
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[180px]">
              <Select value={selectedEventType} onValueChange={setSelectedEventType}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {eventTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={startDate}
                onChange={event => setStartDate(event.target.value)}
                className="h-9"
              />
              <span className="text-xs text-slate-400">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={event => setEndDate(event.target.value)}
                className="h-9"
              />
            </div>
          </div>
          {(selectedEventType !== 'all' || startDate || endDate) && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-500">Filters:</span>
              {selectedEventType !== 'all' && (
                <Badge
                  variant="secondary"
                  className="bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                >
                  {eventTypeOptions.find(o => o.value === selectedEventType)?.label || selectedEventType}
                  <button
                    onClick={() => setSelectedEventType('all')}
                    className="ml-1.5 hover:text-slate-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {(startDate || endDate) && (
                <Badge
                  variant="secondary"
                  className="bg-slate-100 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
                >
                  Date: {startDate || 'Any'} — {endDate || 'Any'}
                  <button
                    onClick={() => { setStartDate(''); setEndDate(''); }}
                    className="ml-1.5 hover:text-slate-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <button
                onClick={() => {
                  setSelectedEventType('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="text-xs text-brand-teal hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}

      {isInitialLoading ? (
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
        </div>
      ) : isEmpty ? (
        <div className="p-8 text-center">
          <History className="h-8 w-8 text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">
            {hasActiveFilters ? 'No events match the selected filters' : 'No activity yet'}
          </p>
        </div>
      ) : (
      <div className="p-4">
        <div className="relative">
          <div className="absolute top-0 left-3.5 w-[1px] h-full bg-slate-200" />

          <div className="space-y-3">
            {events.map(event => {
              const config = eventConfig[event.event_type] || eventConfig.other;
              const updatedFieldLabels = Array.isArray(event.metadata?.updatedFieldLabels)
                ? event.metadata.updatedFieldLabels.filter(label => typeof label === 'string')
                : [];
              const shouldShowExpandableDetails = event.event_type === 'patient_updated' && updatedFieldLabels.length > 3;
              const isExpanded = expandedEventIds.has(event.id);
              const summaryDescription = shouldShowExpandableDetails
                ? `${updatedFieldLabels.length} fields updated`
                : formatTimelineProgramText(event.description);

              return (
                <div key={event.id} className="relative pl-10">
                  <div
                    className={`absolute left-0 top-0.5 w-7 h-7 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center ${config.color}`}
                  >
                    {config.icon}
                  </div>

                  <div className="bg-slate-50 rounded-lg border border-slate-100 px-4 py-3">
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <h4 className="text-sm font-medium text-slate-900">
                          {formatTimelineProgramText(event.title)}
                          {event.actor_name && (
                            <span className="ml-2 text-xs font-normal text-slate-500">
                              by {event.actor_name}
                            </span>
                          )}
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          {formatToLocalDate(event.event_timestamp, 'MMM DD, YYYY hh:mm A')}
                        </p>
                      </div>
                    </div>

                    {summaryDescription && (
                      <p className="text-xs text-slate-600 mt-2">{summaryDescription}</p>
                    )}

                    {shouldShowExpandableDetails && (
                      <div className="mt-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900"
                          onClick={() => toggleExpandedEvent(event.id)}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="h-3.5 w-3.5 mr-1" />
                              Hide details
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3.5 w-3.5 mr-1" />
                              View detailed changes
                            </>
                          )}
                        </Button>

                        {isExpanded && (
                          <div className="mt-2 rounded-md border border-slate-200 bg-white p-2">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 mb-1">
                              Updated Fields
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {updatedFieldLabels.map(label => (
                                <span
                                  key={`${event.id}-${label}`}
                                  className="inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700"
                                >
                                  {label}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {enablePagination && hasMore && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default PatientAuditTrail;
